import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, X, Sparkles, MapPin, ArrowRight } from "lucide-react";
import axios from "axios";

import { SEO } from "../components/SEO";
import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BusinessCard, BusinessCardFull } from "../components/BusinessCard";
import SmartSearch, { getUserTown } from "../components/SmartSearch";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Directory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [towns, setTowns] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [smartResults, setSmartResults] = useState(null);
  const [aiMessage, setAiMessage] = useState("");
  const [relatedSuggestions, setRelatedSuggestions] = useState([]);

  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "all");
  const [selectedTown, setSelectedTown] = useState(searchParams.get("town") || "all");
  const [showFilters, setShowFilters] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchCategories();
    const isSmart = searchParams.get("smart") === "1";
    const query = searchParams.get("search") || "";
    if (isSmart && query) {
      fetchSmartResults(query);
    } else {
      const initialCategory = searchParams.get("category") || "";
      const initialTown = searchParams.get("town") || "";
      const initialSearch = searchParams.get("search") || "";
      fetchBusinesses(initialCategory, initialTown, initialSearch);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only init from URL params

  // Refetch from server when filters change (using debounced search)
  useEffect(() => {
    if (!smartResults) {
      fetchBusinesses(
        selectedCategory === "all" ? "" : selectedCategory,
        selectedTown === "all" ? "" : selectedTown,
        debouncedSearch
      );
    }
  }, [selectedCategory, selectedTown, debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchBusinesses is stable, smartResults intentionally excluded

  const fetchSmartResults = async (query) => {
    setLoading(true);
    try {
      const userTown = getUserTown();
      const res = await axios.post(`${API}/search/ai`, { query, user_town: userTown });
      const data = res.data;
      
      if (data.results && data.results.length > 0) {
        setSmartResults(data.results);
        setFilteredBusinesses(data.results);
        if (data.intent?.ai_message) {
          setAiMessage(data.intent.ai_message);
        } else if (data.intent?.detected_category && data.intent?.detected_town) {
          setAiMessage(`Showing ${data.intent.detected_category} in ${data.intent.detected_town}`);
        }
        setRelatedSuggestions(data.related || []);
      } else {
        // Fallback to regular search
        await fetchBusinesses();
      }
    } catch (e) {
      console.error("Smart search error:", e);
      await fetchBusinesses();
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
      setTowns(response.data.towns);
    } catch (e) {
      console.error("Error fetching categories:", e);
    }
  };

  const fetchBusinesses = async (category = "", town = "", search = "") => {
    setLoading(true);
    try {
      const params = { page_size: 300 };
      if (category) params.category = category;
      if (town) params.town = town;
      if (search) params.search = search;
      
      const response = await axios.get(`${API}/businesses`, { params });
      setBusinesses(response.data);
      setFilteredBusinesses(response.data);
      setLoading(false);
    } catch (e) {
      console.error("Error fetching businesses:", e);
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedTown("all");
    setSmartResults(null);
    setAiMessage("");
    setRelatedSuggestions([]);
    setSearchParams({});
    // fetchBusinesses will be triggered by useEffect when state changes
  };

  const hasActiveFilters = searchQuery || (selectedCategory && selectedCategory !== "all") || (selectedTown && selectedTown !== "all");

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO 
        title="Business Directory"
        description="Browse all businesses, services, and experiences in East Kootenay, BC. Filter by category and town - Golden, Invermere, Cranbrook, Fernie, Creston, Kimberley and more."
        keywords="East Kootenay businesses, Invermere directory, Cranbrook businesses, Fernie directory, local services BC"
      />
      <NavbarLight />

      {/* Header */}
      <section className="pt-8 pb-6 px-6 md:px-12 border-b-2 border-[#D1CFC5]">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1A1915] mb-2">
            Business Directory
          </h1>
          <p className="text-[#5C5A52] text-lg">
            Every business, service, and experience in East Kootenay.
          </p>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="py-6 px-6 md:px-12 bg-[#F7F6F2] border-b-2 border-[#D1CFC5]">
        <div className="max-w-7xl mx-auto">
          {/* Search Bar */}
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C5A52]" />
              <Input
                type="text"
                placeholder="Search businesses..."
                className="pl-12 bg-white border-2 border-[#D1CFC5] py-3 rounded-lg w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="directory-search-input"
              />
            </div>

            {/* Desktop Filters */}
            <div className="hidden md:flex items-center gap-3">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48 bg-white border-2 border-[#D1CFC5]" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedTown} onValueChange={setSelectedTown}>
                <SelectTrigger className="w-48 bg-white border-2 border-[#D1CFC5]" data-testid="town-filter">
                  <SelectValue placeholder="All Towns" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Towns</SelectItem>
                  {towns.map(town => (
                    <SelectItem key={town} value={town}>{town}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="text-[#5C5A52] hover:text-[#1A1915]"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* Mobile Filter Toggle */}
            <Button 
              className="md:hidden bg-white border-2 border-[#D1CFC5] text-[#1A1915]"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 w-2 h-2 bg-[#D4A017] rounded-full" />
              )}
            </Button>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="md:hidden mt-4 pt-4 border-t border-[#D1CFC5] space-y-3">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full bg-white border-2 border-[#D1CFC5]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedTown} onValueChange={setSelectedTown}>
                <SelectTrigger className="w-full bg-white border-2 border-[#D1CFC5]">
                  <SelectValue placeholder="All Towns" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Towns</SelectItem>
                  {towns.map(town => (
                    <SelectItem key={town} value={town}>{town}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button 
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="py-10 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* AI Message */}
          {aiMessage && (
            <div className="flex items-center gap-2 mb-4 bg-[#2C4A3B]/5 border border-[#2C4A3B]/20 rounded-lg px-4 py-3" data-testid="ai-search-message">
              <Sparkles className="w-4 h-4 text-[#D4A017] flex-shrink-0" />
              <span className="text-sm text-[#1A1915]">{aiMessage}</span>
              <button onClick={clearFilters} className="ml-auto text-xs text-[#5C5A52] hover:text-[#1A1915]">Clear</button>
            </div>
          )}

          {/* Related Suggestions */}
          {relatedSuggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-xs text-[#5C5A52]">Also try:</span>
              {relatedSuggestions.map((s, i) => (
                <button
                  key={`${s.type}-${s.value}`}
                  onClick={() => {
                    if (s.type === "category") setSelectedCategory(s.value);
                    else if (s.type === "town") setSelectedTown(s.value);
                    setSmartResults(null);
                    setAiMessage("");
                    setRelatedSuggestions([]);
                  }}
                  className="inline-flex items-center gap-1 text-xs bg-[#F7F6F2] border border-[#D1CFC5] rounded-full px-3 py-1 hover:border-[#2C4A3B] transition-colors"
                >
                  {s.type === "town" ? <MapPin className="w-3 h-3" /> : null}
                  {s.label}
                  <ArrowRight className="w-2.5 h-2.5" />
                </button>
              ))}
            </div>
          )}

          {/* Results Count */}
          <p className="text-[#5C5A52] mb-6">
            {loading ? "Loading..." : `${filteredBusinesses.length} business${filteredBusinesses.length !== 1 ? "es" : ""} found`}
            {smartResults && " (smart search)"}
          </p>

          {/* Business Grid */}
          {!loading && filteredBusinesses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBusinesses.map(business => (
                <BusinessCard 
                  key={business.id}
                  business={business} 
                  onClick={() => setSelectedBusiness(business)}
                />
              ))}
            </div>
          ) : !loading && (
            <div className="text-center py-20">
              <p className="text-[#5C5A52] text-lg mb-4">No businesses found matching your criteria.</p>
              <Button onClick={clearFilters} className="bg-[#2C4A3B] text-white">
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* Business Modal */}
      {selectedBusiness && (
        <BusinessCardFull 
          business={selectedBusiness} 
          onClose={() => setSelectedBusiness(null)} 
        />
      )}
    </div>
  );
};

export default Directory;
